import React from 'react';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import { gettext, translationPropType } from '@eventbrite/i18n';
import {
    uploadImageFile,
    updateImage,
    transformToApiCropMask,
    IMAGE_TYPES,
} from '@eventbrite/http';

import { ImageUploader } from '@eventbrite/eds-image-uploader';
import { ValidationFormField } from '@eventbrite/eds-validation-form-field';
import { SVG_PROP_TYPE } from '@eventbrite/eds-vector-image';
import { CROP_MASK_PROP_TYPE } from '@eventbrite/eds-image-cropper';
import { getCroppedImageDataURL } from '@eventbrite/eds-utils';
import { withOverlayControlsHOC } from '@eventbrite/eds-structure';
import { TYPE_COMPLEX } from '@eventbrite/eds-modal';
import { STYLE_FILL, STYLE_NEUTRAL } from '@eventbrite/eds-button';
import { NotificationBar } from '@eventbrite/eds-notification';
import { TYPE_ERROR } from '@eventbrite/eds-notification';

import ConstrainedHeightCropper from './_internal/ConstrainedHeightCropper';
import {
    CROP_DISALLOWED,
    CROP_REQUIRED,
    CROP_ONLY_INITIAL,
    CROP_INTENTIONS,
    REQUIRED_CROP_CANCELED,
} from './constants';
import {
    transformUploadFailureMessage,
    conditionalDeepCompare,
    pickPropsByTypes,
} from './utils';

const ImageUploaderField = ({ value, ...props }) => (
    <ImageUploader
        {...pickPropsByTypes(props, ImageUploader.propTypes)}
        imageUrl={value}
    />
);

const CropperError = ({ error }) => {
    if (!error) {
        return null;
    }

    return (
        <div className="eds-l-mar-bot-2" data-spec="cropper-modal-error">
            <NotificationBar type={TYPE_ERROR} shouldScrollTo={true}>
                {error}
            </NotificationBar>
        </div>
    );
};

const DEFAULT_CROP_STATE = {
    continueUpload: noop,
    cancelUpload: noop,
    setCroppedImage: noop,
    workingCropMask: undefined,
    showCropper: false,
    cropperImageUrl: undefined,
    isEditingCrop: false,
    cropIsSaving: false,
};

class ImageField extends React.Component {
    static propTypes = {
        /**
         * Any components you want to display within the ImageField.
         */
        children: PropTypes.node,

        /**
         * File object to upload programmatically.
         */
        file: PropTypes.object,

        /**
         * By default is true and means that component will have a validation field. Passing this flag as false allows to use this component without redux and validation field functionallity.
         */
        needsValidationField: PropTypes.bool,
        /**
         * Updates form field values based on new image field data
         * (from calling `connectImageField` in `@eventbrite/redux-image-field`)
         */
        updateFieldsFromObject: PropTypes.func.isRequired,
        /**
         * Name of the image form field
         * (from calling `connectImageField` in `@eventbrite/redux-image-field`)
         */
        name: PropTypes.string.isRequired,
        /**
         * ID of existing image
         * (from calling `connectImageField` in `@eventbrite/redux-image-field`)
         */
        imageId: PropTypes.string,
        /**
         * URL to image (used in cropping)
         * (from calling `connectImageField` in `@eventbrite/redux-image-field`)
         */
        originalUrl: PropTypes.string,
        /**
         * Crop mask to apply
         * (from calling `connectImageField` in `@eventbrite/redux-image-field`)
         */
        cropMask: CROP_MASK_PROP_TYPE,

        /**
         * The API image type
         */
        imageType: PropTypes.oneOf(IMAGE_TYPES).isRequired,
        /**
         * Alt attribute provided to the underlying image tag.
         */
        imageAlt: translationPropType.isRequired,
        /**
         * Hidden accessibility label
         */
        hiddenLabel: translationPropType.isRequired,
        /**
         * Whether or not cropping is mandatory, optional, or disallowed
         */
        cropIntention: PropTypes.oneOf(CROP_INTENTIONS),
        /**
         * Desired aspect ratio of the resulting crop.
         */
        cropAspectRatio: PropTypes.number,
        /**
         * Default image url
         */
        imageUrl: PropTypes.string,
        /**
         * Primary text shown in the initial uploader state.
         * This should be a call-to-action to the user ("Choose an Image")
         */
        headline: translationPropType,
        /**
         * Secondary text shown in the initial uploader state.
         * This can be longer descriptive text to help the user choose an appropriate image.
         */
        description: PropTypes.node,
        /**
         * Icon shown above the headline in the initial uploader state.
         */
        iconType: SVG_PROP_TYPE,
        /**
         * The maximum acceptable filesize specified in megabytes.
         * Files larger than this number will be rejected with an error message.
         */
        maxFileSize: PropTypes.number,
        /**
         * A list of acceptable content-types for dropped files.
         * Files of any type other than those in this list will be rejected with an error message.
         */
        allowedContentTypes: PropTypes.arrayOf(PropTypes.string),
        /**
         * The error message shown to the user if they drop an inappropriate filetype.
         * This can be specified via props to provide a more specific actionable message
         * such as "Please choose a JPG, PNG, or GIF"
         */
        contentTypeError: translationPropType,
        /**
         * The amount of spacing for the bottom
         */
        bottomSpacing: PropTypes.number,
        /**
         * Function to determine whether we should
         * display an error given the input state. Has a sensible default.
         */
        shouldDisplayError: PropTypes.func,
        /**
         * Whether the component should display cover or not.
         *
         * Not displaying the cover means that the image uploader
         * will not be clickable. Drag and drop files will still work
         * though.
         */
        shouldDisplayCover: PropTypes.bool,
        /**
         * Function that's invoked when the user cancels the image crop
         */
        onCancelCrop: PropTypes.func,
        /**
         * Function that's invoked when the user has chosen a file
         */
        onReceiveFile: PropTypes.func,
        /**
         * Function that's invoked when the user clicks "Remove"
         */
        onRemoveImage: PropTypes.func,
        /**
         * Function that's invoked when the user clicks "Crop" button.
         */
        onEditImage: PropTypes.func,
        /**
         * Function that's invoked when the user has chosen a file and returns
         * the status of the ImageUploader process of uploading.
         * Returns 3 possibles strings: 'uploading', 'uploaded', 'failed'.
         */
        onUploadStatusChange: PropTypes.func,

        /**
         * This boolean forces the cleaning of image validators, if set to true.
         */
        shouldCleanValidationErrors: PropTypes.bool,

        // From HOCs
        showOverlay: PropTypes.func,

        /**
         * This boolean controls whether the component should display the image requirements below the image field. If this is set to true, specific errors will also be highlighted on the requirements display.
         */
        useRequirementsDisplay: PropTypes.bool,
    };

    static defaultProps = {
        cropIntention: CROP_DISALLOWED,
        shouldDisplayCover: true,
        needsValidationField: true,
    };

    state = {
        ...DEFAULT_CROP_STATE,
        cropError: undefined,
    };

    shouldComponentUpdate(nextProps, nextState) {
        return (
            !conditionalDeepCompare(this.props, nextProps, 'cropMask') ||
            !conditionalDeepCompare(this.state, nextState, 'workingCropMask')
        );
    }

    componentDidUpdate() {
        const { showOverlay, imageAlt, cropAspectRatio, cropMask } = this.props;
        const { showCropper, cropperImageUrl } = this.state;

        /**
         * We need to set an intention to show the cropper before `cropperImageUrl` is
         * available when uploading a new image.
         * During initial upload:
         *      A synchronous process (user chooses the file) will set `showCropper`.
         *      An async process (loading image URL from local file) will set `cropperImageUrl`.
         * We should only show the modal when both are fulfilled.
         * If crop isn't required, `cropperImageUrl` will be set but no crop is needed.
         * Therefore, we should check both to see if we should show cropper.
         *
         * During edit, both `showCropper` and `cropperImageUrl` will be set at the same time.
         */
        if (showCropper && cropperImageUrl) {
            showOverlay('modal', {
                type: TYPE_COMPLEX,
                title: gettext('Crop Image'),
                onClose: this._handleCropCancel.bind(this),
                onPrimaryAction: this._handleCropSave.bind(this),
                primaryText: gettext('Save'),
                primaryType: STYLE_FILL,
                onSecondaryAction: this._handleCropCancel.bind(this),
                secondaryText: gettext('Cancel'),
                secondaryType: STYLE_NEUTRAL,
                children: (
                    <ConstrainedHeightCropper
                        imageSrc={cropperImageUrl}
                        onCropDone={this._handleCrop.bind(this)}
                        imageAltText={imageAlt}
                        aspectRatio={cropAspectRatio}
                        cropMask={cropMask}
                    />
                ),
            });
        }
    }

    _handleCrop(workingCropMask) {
        this.setState({ workingCropMask });
    }

    _interceptProgressImage(cropperImageUrl, setCroppedImage) {
        this.setState({
            setCroppedImage,
            cropperImageUrl,
        });
    }

    _handleProgressImageLoaded(progressImage) {
        return new Promise(
            this._interceptProgressImage.bind(this, progressImage),
        );
    }

    _interceptUploadForCropIntention(continueUpload, cancelUpload) {
        const { cropIntention } = this.props;

        if (
            cropIntention !== CROP_REQUIRED &&
            cropIntention !== CROP_ONLY_INITIAL
        ) {
            continueUpload();
            return;
        }

        this.setState({
            continueUpload,
            cancelUpload: cancelUpload.bind(null, REQUIRED_CROP_CANCELED),
            showCropper: true,
            isEditingCrop: false,
        });
    }

    _performUpload(file, onProgress, cropMask) {
        const { imageType } = this.props;
        let apiCropMask;

        if (cropMask) {
            apiCropMask = transformToApiCropMask(cropMask);
        }

        return uploadImageFile(file, imageType, onProgress, apiCropMask);
    }

    _handleUploadSuccess(apiImage) {
        const { updateFieldsFromObject } = this.props;
        const { localCroppedImage } = this.state;
        const imageUrl = apiImage?.url || localCroppedImage;

        updateFieldsFromObject({
            ...apiImage,
            url: imageUrl,
        });
        return imageUrl;
    }

    _handleReceiveFile(file, onProgress) {
        const { onReceiveFile } = this.props;

        if (onReceiveFile) {
            onReceiveFile();
        }

        return new Promise(this._interceptUploadForCropIntention.bind(this))
            .then(this._performUpload.bind(this, file, onProgress))
            .then(this._handleUploadSuccess.bind(this))
            .catch(transformUploadFailureMessage);
    }

    _handleRemoveImage() {
        const { updateFieldsFromObject, onRemoveImage } = this.props;

        if (onRemoveImage) {
            onRemoveImage();
        }

        updateFieldsFromObject();
        this.setState({
            cropError: undefined,
        });
    }

    _handleCropCancel() {
        const { cancelUpload } = this.state;
        const { onCancelCrop } = this.props;

        cancelUpload();
        this._resetCropState();
        onCancelCrop && onCancelCrop();
    }

    _handleNewCropSave(cropMask) {
        const { cropperImageUrl, continueUpload, setCroppedImage } = this.state;

        getCroppedImageDataURL(cropperImageUrl, cropMask).then((dataUri) => {
            setCroppedImage(dataUri);
            this.setState({ localCroppedImage: dataUri });
        });
        continueUpload(cropMask);
        this._resetCropState();
    }

    _resetCropState() {
        this.setState(DEFAULT_CROP_STATE);
    }

    _handleEditCrop() {
        const { originalUrl, onEditImage } = this.props;

        if (onEditImage) {
            onEditImage();
        }

        this.setState({
            showCropper: true,
            isEditingCrop: true,
            cropperImageUrl: originalUrl,
            cropError: undefined,
        });
    }

    _handleEditCropSave(cropMask) {
        const { imageId } = this.props;

        this.setState({
            cropIsSaving: true,
            showCropper: false,
        });
        updateImage(imageId, transformToApiCropMask(cropMask))
            .then(this._handleEditCropSaveSuccess.bind(this))
            .catch(this._handleEditCropSaveFailure.bind(this));
    }

    _handleEditCropSaveSuccess(apiImage) {
        const { updateFieldsFromObject } = this.props;

        updateFieldsFromObject(apiImage);
        this._resetCropState();
    }

    _handleEditCropSaveFailure() {
        this.setState({
            cropError: gettext('Unable to save your crop, please try again'),
            cropIsSaving: false,
        });
        this._resetCropState();
    }

    _handleCropSave() {
        const { isEditingCrop, workingCropMask } = this.state;

        if (isEditingCrop) {
            return this._handleEditCropSave(workingCropMask);
        }

        return this._handleNewCropSave(workingCropMask);
    }

    render() {
        const {
            allowedContentTypes,
            bottomSpacing,
            children,
            contentTypeError,
            cropAspectRatio,
            cropIntention,
            description,
            headline,
            hiddenLabel,
            iconType,
            imageAlt,
            imageUrl,
            maxFileSize,
            name,
            onUploadStatusChange,
            shouldCleanValidationErrors,
            shouldDisplayCover,
            shouldDisplayError,
            useRequirementsDisplay,
        } = this.props;
        const { cropError } = this.state;
        let onCrop;

        if (
            cropIntention !== CROP_DISALLOWED &&
            cropIntention !== CROP_ONLY_INITIAL
        ) {
            onCrop = this._handleEditCrop.bind(this);
        }

        const handledImageUploaderField = (
            <ImageUploaderField
                value={imageUrl}
                file={this.props.file}
                onReceiveFile={this._handleReceiveFile.bind(this)}
                onRemove={this._handleRemoveImage.bind(this)}
                onProgressImageLoaded={this._handleProgressImageLoaded.bind(
                    this,
                )}
                onCrop={onCrop}
                onUploadStatusChange={onUploadStatusChange}
                headline={headline}
                description={description}
                iconType={iconType}
                maxFileSize={maxFileSize}
                allowedContentTypes={allowedContentTypes}
                contentTypeError={contentTypeError}
                imageAlt={imageAlt}
                shouldDisplayCover={shouldDisplayCover}
                shouldCleanValidationErrors={shouldCleanValidationErrors}
                useRequirementsDisplay={useRequirementsDisplay}
                cropAspectRatio={cropAspectRatio}
            >
                {children}
            </ImageUploaderField>
        );

        return (
            <div>
                <CropperError error={cropError} />
                {this.props.needsValidationField ? (
                    <ValidationFormField
                        name={`${name}Url`}
                        label={hiddenLabel}
                        hideLabel={true}
                        bottomSpacing={bottomSpacing}
                        shouldDisplayError={shouldDisplayError}
                    >
                        {handledImageUploaderField}
                    </ValidationFormField>
                ) : (
                    handledImageUploaderField
                )}
            </div>
        );
    }
}

export default withOverlayControlsHOC(ImageField);
